import type { Column } from '@tanstack/react-table'
import { useMemo } from 'react'
import type { ExtendedColumnDef, FilterConfig, FilterType } from '../types/data-table.types'
import { useFilterTypeGuards } from './use-filter-type-guards'

/**
 * Helper function to get column title
 */
function getColumnTitle<TData>(
  column: Column<TData, unknown> & { columnDef: ExtendedColumnDef<TData, unknown> },
): string {
  const header = column.columnDef.header
  if (typeof header === 'string') {
    return header
  }

  const columnDef = column.columnDef
  if (columnDef.title && typeof columnDef.title === 'string') {
    return columnDef.title
  }

  return column.id.charAt(0).toUpperCase() + column.id.slice(1)
}

export function useFilterConfig<TData>(columns: Column<TData, unknown>[]) {
  const { hasFilterConfig, isFilterConfig } = useFilterTypeGuards()

  return useMemo(() => {
    const filterableColumns = columns.filter((column) => {
      if (!hasFilterConfig(column.columnDef)) return false

      const config = column.columnDef.filterConfig
      if (!isFilterConfig(config) || Object.keys(config).length === 0) return false

      return column.getCanFilter() && config.filterable !== false
    })

    const getFilterConfig = (column: Column<TData, unknown>): FilterConfig => {
      if (!hasFilterConfig(column.columnDef)) return { filterable: false }
      const config = column.columnDef.filterConfig
      if (!isFilterConfig(config) || Object.keys(config).length === 0) {
        return { filterable: false }
      }
      return config
    }

    const getFilterType = (column: Column<TData, unknown>): FilterType => {
      const config = getFilterConfig(column)
      return config.filterType || 'text'
    }

    const getFilterTitle = (
      column: Column<TData, unknown> & { columnDef: ExtendedColumnDef<TData, unknown> },
    ): string => {
      if (hasFilterConfig(column.columnDef)) {
        const filterConfig = column.columnDef.filterConfig
        if (
          isFilterConfig(filterConfig) &&
          filterConfig.filterTitle &&
          typeof filterConfig.filterTitle === 'string'
        ) {
          return filterConfig.filterTitle
        }
      }

      return getColumnTitle(column)
    }

    const getFilterPlaceholder = (column: Column<TData, unknown>): string => {
      const config = getFilterConfig(column)
      return config.filterPlaceholder || `Search in ${getFilterTitle(column)}`
    }

    const getFilterOptions = (column: Column<TData, unknown>) => {
      const config = getFilterConfig(column)
      return config.filterOptions || []
    }

    const hasCustomFilter = (column: Column<TData, unknown>): boolean => {
      const config = getFilterConfig(column)
      return config.filterType === 'custom' && !!config.customFilter
    }

    return {
      filterableColumns,
      getFilterConfig,
      getFilterType,
      getFilterTitle,
      getFilterPlaceholder,
      getFilterOptions,
      hasCustomFilter,
    }
  }, [columns, hasFilterConfig, isFilterConfig])
}
